import { getLowercaseCountryCodeFromIntlLocale, localeToIntlLocale } from "@core/i18n/locale-helper";
import { InfraManager } from "@domain/infra/infra-manager";
import {
	ProspectRequestConfig,
	ProspectRequestReferential,
	ProspectRequestReferentialsMap,
} from "@domain/prospect-request/prospect-request";
import { ProspectRequestService } from "@domain/prospect-request/prospect-request-service";

import { CountryService } from "@domain/localization/country-service";
import { DemoRequestFormData } from "@ui/pages/prospect/campaigns/demo-request/demo-request-form";
import { InviteYourPtFormData } from "@ui/pages/prospect/campaigns/invite-your-pt/invite-your-pt-form";
import { isNotNil } from "@utils/assert";

export class ProspectRequestManager {
	constructor(
		private readonly prospectRequestService: ProspectRequestService,
		private readonly countryService: CountryService,
		private readonly infraManager: InfraManager,
	) {}

	async prepareProspectRequest(inputLocale?: string): Promise<ProspectRequestConfig> {
		const supportedCountries = await this.countryService.getSupportedCountries();

		const initialCountryCode = await this.getBestCountryFromSupportedCountries(supportedCountries, inputLocale);

		const referentialsMap = await this.fetchCountriesReferentials(supportedCountries);

		await this.selectCountry(initialCountryCode);

		return {
			supportedCountries,
			initialCountryCode,
			referentialsMap,
		};
	}

	async selectCountry(countryCode: string): Promise<void> {
		await this.infraManager.changeInfrastructureUsingCountryCode(countryCode);
	}

	async sendDemoRequest(demoRequest: DemoRequestFormData): Promise<void> {
		await this.prospectRequestService.sendDemoRequest(demoRequest);
	}

	async sendInviteYourPTRequest(inviteYourPtRequest: InviteYourPtFormData): Promise<void> {
		await this.prospectRequestService.sendInviteYourPTRequest(inviteYourPtRequest);
	}

	private async getBestCountryFromSupportedCountries(
		supportedCountries: string[],
		inputLocale?: string,
	): Promise<string> {
		let bestCountryCode: string | undefined;
		if (isNotNil(inputLocale)) {
			const inputCountryCode = getLowercaseCountryCodeFromIntlLocale(localeToIntlLocale(inputLocale));
			bestCountryCode = supportedCountries.find(countryCode => countryCode === inputCountryCode);
		}
		if (isNotNil(bestCountryCode)) {
			return bestCountryCode;
		}

		return await this.countryService.getDefaultCountry();
	}

	private async fetchCountriesReferentials(countryCodes: string[]): Promise<ProspectRequestReferentialsMap> {
		const referentialsMap = new Map();
		for (const countryCode of countryCodes) {
			const referential = await this.fetchCountryReferential(countryCode);
			referentialsMap.set(countryCode, referential);
		}
		return referentialsMap;
	}

	private async fetchCountryReferential(countryCode: string): Promise<ProspectRequestReferential> {
		if (!this.infraManager.targetInfra?.hostedCountries.includes(countryCode)) {
			this.infraManager.changeInfrastructureUsingCountryCode(countryCode);
		}
		return await this.countryService.getCountryUserConfig(countryCode);
	}
}
